/**
 * Pure URL-parameter parsing for `/n3?mesureIds=<id1>,<id2>,...`
 * (`ADR_N1_WIDGET_NAVIGATION.md` §W2/§W4 "Format d'URL", Phase 9C.3B.5 —
 * this is the technical prerequisite the ADR explicitly deferred, see its
 * "Follow-up Actions").
 *
 * `buildN3Href` (`features/n1/n1-navigation.ts`) individually
 * `encodeURIComponent`-encodes each id before joining with a literal
 * comma. The browser/router already fully percent-decodes a query
 * parameter's value before handing it to application code (React Router's
 * `useSearchParams().get("mesureIds")` wraps the standard `URLSearchParams`
 * decoding) — that is "the router's own decoding convention" this parser
 * relies on. `safeDecodeSegment` below only adds a second, defensive
 * `decodeURIComponent` pass per comma-separated segment for the case a
 * caller (or a future direct link) still hands this function a
 * partially-encoded segment (e.g. a literal `%20`); it never throws on a
 * malformed sequence — it just falls back to the raw segment, since a
 * decoding failure is never a reason to invent or drop an id.
 */

function safeDecodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Reads `/n3?mesureIds=...` — a comma-separated list of `mesureId`.
 *
 * - `value === null` (no `mesureIds` param at all, e.g. bare `/n3`) → `[]`.
 * - Splits on the literal `,` list separator (the exact separator
 *   `buildN3Href` joins with), never on whitespace alone.
 *   Each segment is defensively decoded, then trimmed of surrounding
 *   whitespace.
 * - Empty segments (`""`, or whitespace-only before trimming) are dropped
 *   — covers `mesureIds=`, `mesureIds=,,,`, and `mesureIds=%20,%20`.
 * - Duplicate ids are removed, keeping only the *first* occurrence's
 *   position (`Set`-based, single left-to-right pass) — "préserver
 *   l'ordre de première apparition".
 * - Never invents an id, never picks only the first id: every distinct,
 *   non-empty id survives, in the order it first appeared.
 */
export function parseMesureIdsParam(value: string | null): string[] {
  if (value === null) return [];

  const seen = new Set<string>();
  const result: string[] = [];

  for (const rawSegment of value.split(",")) {
    const id = safeDecodeSegment(rawSegment).trim();
    if (id === "") continue;
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }

  return result;
}
